import * as THREE from "three";
import type { SuntraceQuality } from "./types";

const SUNTRACE_SHADOW_FALLBACK_LIGHT_DIRECTION = new THREE.Vector3(
  0.82,
  0.23,
  -0.51,
).normalize();

export type SuntraceShadowProfile = {
  enabled: boolean;
  mapSize: number;
  extent: number;
  near: number;
  far: number;
  worldTexelSize: number;
  normalBias: number;
};

export const createSuntraceShadowProfile = (
  quality: SuntraceQuality,
): SuntraceShadowProfile => {
  const mapSize = quality === "high" ? 2_048 : quality === "medium" ? 1_024 : 0;
  const extent = quality === "high" ? 58 : 64;
  const worldTexelSize = mapSize === 0 ? 0 : extent * 2 / mapSize;
  return {
    enabled: mapSize > 0,
    mapSize,
    extent,
    near: 8,
    far: 360,
    worldTexelSize,
    normalBias: worldTexelSize * 0.34,
  };
};

export const applySuntraceShadowProfile = (
  sun: THREE.DirectionalLight,
  profile: SuntraceShadowProfile,
): void => {
  if (sun.castShadow !== profile.enabled) {
    sun.castShadow = profile.enabled;
  }
  if (sun.shadow.mapSize.width !== profile.mapSize) {
    sun.shadow.map?.dispose();
    sun.shadow.map = null;
    sun.shadow.mapSize.set(profile.mapSize, profile.mapSize);
  }
  const camera = sun.shadow.camera;
  const boundsChanged =
    camera.near !== profile.near ||
    camera.far !== profile.far ||
    camera.left !== -profile.extent ||
    camera.right !== profile.extent ||
    camera.top !== profile.extent ||
    camera.bottom !== -profile.extent;
  if (boundsChanged) {
    camera.near = profile.near;
    camera.far = profile.far;
    camera.left = -profile.extent;
    camera.right = profile.extent;
    camera.top = profile.extent;
    camera.bottom = -profile.extent;
    camera.updateProjectionMatrix();
  }
  if (sun.shadow.normalBias !== profile.normalBias) {
    sun.shadow.normalBias = profile.normalBias;
  }
};

export const snapSuntraceShadowAnchor = (
  position: THREE.Vector3,
  lightDirection: THREE.Vector3,
  profile: SuntraceShadowProfile,
  target: THREE.Vector3,
): THREE.Vector3 => {
  if (!profile.enabled) {
    return target.copy(position);
  }

  const forward = lightDirection.lengthSq() > Number.EPSILON
    ? lightDirection.clone().normalize()
    : SUNTRACE_SHADOW_FALLBACK_LIGHT_DIRECTION;
  const reference = Math.abs(forward.y) > 0.99
    ? new THREE.Vector3(1, 0, 0)
    : new THREE.Vector3(0, 1, 0);
  const right = new THREE.Vector3().crossVectors(reference, forward).normalize();
  const up = new THREE.Vector3().crossVectors(forward, right).normalize();
  const depth = position.dot(forward);
  const horizontal = Math.round(position.dot(right) / profile.worldTexelSize) * profile.worldTexelSize;
  const vertical = Math.round(position.dot(up) / profile.worldTexelSize) * profile.worldTexelSize;

  return target
    .copy(forward)
    .multiplyScalar(depth)
    .addScaledVector(right, horizontal)
    .addScaledVector(up, vertical);
};
